/**
 * proposal_pdf.js
 *  - モデルケース5本をベースにA4 PDF提案書を生成するモック機能
 *  - デザインは最小限（「moc仮導入」指示）。次回以降に磨き込み
 *  - 依存: jsPDF(UMD 2.5.1) ※ result.html 側で既に読み込まれている前提
 *
 * 既存 sim_engine.js のロジックは一切触らない。
 *
 * 制約: jsPDF標準フォントは日本語未対応のため、テキストはすべて
 *       日本語文字を画像化（Canvas→addImage）してPDFへ貼付する。
 *       外部フォント埋込（Noto Sans JP）は次回Agentのタスク。
 */

// ============================================================================
// proposal_pdf.js (ESM版)
// 元データ: 260418 [EG]自家消費シミュレーター/mock/js/proposal_pdf.js
// 移植日: 2026-04-27 (Phase 1 Cloudflare 移植のためESM化)
// 依存: jspdf (npm import)
// ロジック本体は完全保持、loadModelCases だけ /api/simulate/model-cases に切替
// ============================================================================
import { jsPDF } from 'jspdf';
import { api } from './api.js';
'use strict';

  const A4 = { w: 210, h: 297 }; // mm
  const MARGIN = { top: 18, right: 14, bottom: 18, left: 14 };
  const LINE_HEIGHT = 6; // mm

  // =========================================================================
  // 1. モデルケース読込（fetch → data/model_cases.json）
  // =========================================================================
  let MODEL_CASES = null;
  async function loadModelCases() {
    if (MODEL_CASES) return MODEL_CASES;
    try {
      const data = await api.modelCases();
      // API response: { items: [{id, code, segment, label, payload, ...}] }
      // 既存実装は { cases: [...] } 形式を期待 → 変換
      const cases = (data.items || []).map((it) => Object.assign({}, it.payload || {}, {
        id: it.code, segment: it.segment, label: it.label,
      }));
      MODEL_CASES = { version: data.meta?.updated_at || '', cases };
      return MODEL_CASES;
    } catch (e) {
      console.error('[proposal_pdf] モデルケースのロードに失敗:', e);
      throw e;
    }
  }

  // =========================================================================
  // 2. 日本語テキストを画像化してPDFへ貼付するユーティリティ
  //    jsPDF標準フォントは日本語NG → Canvas高DPIレンダリング+PNG埋込で精細化
  //    Noto Sans JP は Google Fonts 経由でHTMLに@font-face適用 (proposal.html側)
  //    2026-04-27: scale 2→3 高DPI化、フォント読込完了待機を追加
  // =========================================================================

  // フォント読込完了待機 (proposal.html側で document.fonts.ready を chain)
  let fontReadyPromise = null;
  async function ensureFontReady() {
    if (fontReadyPromise) return fontReadyPromise;
    fontReadyPromise = (async () => {
      try {
        if (typeof document !== 'undefined' && document.fonts && document.fonts.load) {
          // Noto Sans JP を明示load (browserにキャッシュさせる)
          await Promise.all([
            document.fonts.load('400 12px "Noto Sans JP"', 'あ'),
            document.fonts.load('700 12px "Noto Sans JP"', 'あ'),
          ]);
          await document.fonts.ready;
        }
      } catch (e) {
        console.warn('[proposal_pdf] font preload failed (fallback継続):', e);
      }
    })();
    return fontReadyPromise;
  }

  function textToImageBase64(text, opts) {
    opts = opts || {};
    const fontSize = opts.fontSize || 12; // px
    const color = opts.color || '#111';
    const bold = opts.bold || false;
    const weight = bold ? '700' : '400';
    const font = `${weight} ${fontSize}px "Noto Sans JP","Hiragino Kaku Gothic ProN","Yu Gothic Medium","Yu Gothic","Meiryo",sans-serif`;

    // 幅計測
    const measure = document.createElement('canvas').getContext('2d');
    measure.font = font;
    const metrics = measure.measureText(text);
    const scale = 3; // 3倍解像度 (Retina+α、PDF表示時にシャープ)
    const pad = 4;
    const w = Math.max(10, Math.ceil(metrics.width) + pad * 2);
    const h = Math.ceil(fontSize * 1.4) + pad * 2;

    const c = document.createElement('canvas');
    c.width = w * scale;
    c.height = h * scale;
    const ctx = c.getContext('2d');
    ctx.scale(scale, scale);
    // 白背景 (PDFが透明扱いしないよう明示)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    // テキストアンチエイリアス最大化
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textBaseline = 'top';
    ctx.fillText(text, pad, pad);
    return { data: c.toDataURL('image/png'), w: w, h: h };
  }

  function drawText(pdf, text, x, y, opts) {
    if (text === null || text === undefined || text === '') return y;
    const img = textToImageBase64(String(text), opts);
    // px → mm: 1px ≈ 0.264583 mm
    const mmW = img.w * 0.264583;
    const mmH = img.h * 0.264583;
    pdf.addImage(img.data, 'PNG', x, y, mmW, mmH);
    return y + mmH + 1.0;
  }

  function drawLine(pdf, x1, y1, x2, y2, color) {
    pdf.setDrawColor(color || '#cccccc');
    pdf.setLineWidth(0.2);
    pdf.line(x1, y1, x2, y2);
  }

  function drawRect(pdf, x, y, w, h, fill) {
    pdf.setFillColor(fill || '#f1f5f9');
    pdf.setDrawColor('#cccccc');
    pdf.setLineWidth(0.2);
    pdf.rect(x, y, w, h, 'FD');
  }

  function ensureSpace(pdf, y, need) {
    if (y + need > A4.h - MARGIN.bottom) {
      pdf.addPage();
      return MARGIN.top;
    }
    return y;
  }

  function fmtJPY(n) {
    if (n === null || n === undefined || isNaN(n)) return '—';
    return '¥' + Math.round(n).toLocaleString('ja-JP');
  }
  function fmtNum(n, digits) {
    if (n === null || n === undefined || isNaN(n)) return '—';
    return Number(n).toLocaleString('ja-JP', {
      minimumFractionDigits: digits || 0, maximumFractionDigits: digits || 0
    });
  }

  // =========================================================================
  // 3. PDFの各ページ描画
  // =========================================================================
  function renderCoverPage(pdf, cs, meta) {
    let y = 40;
    // タイトルバー
    pdf.setFillColor('#2196f3');
    pdf.rect(0, 0, A4.w, 22, 'F');
    drawText(pdf, 'splight Solar Sim - 提案書（モック版）', MARGIN.left, 6, {
      fontSize: 16, bold: true, color: '#ffffff'
    });

    y = 60;
    y = drawText(pdf, '自家消費型太陽光発電システム ご提案書', MARGIN.left, y, {
      fontSize: 20, bold: true, color: '#111827'
    });
    y += 6;
    y = drawText(pdf, cs.label, MARGIN.left, y, { fontSize: 14, color: '#374151' });

    y += 20;
    drawLine(pdf, MARGIN.left, y, A4.w - MARGIN.right, y, '#2196f3');
    y += 6;

    y = drawText(pdf, 'お客様名：' + (meta.customerName || '＿＿＿＿＿＿＿＿＿＿＿＿＿＿'), MARGIN.left, y, { fontSize: 12 });
    y = drawText(pdf, 'ご担当者：' + (meta.contactName || '＿＿＿＿＿＿＿＿'), MARGIN.left, y, { fontSize: 12 });
    y = drawText(pdf, '発行日　：' + (meta.date || new Date().toISOString().slice(0, 10)), MARGIN.left, y, { fontSize: 12 });
    y = drawText(pdf, '作成者　：splight株式会社 EG部', MARGIN.left, y, { fontSize: 12 });

    y = A4.h - 40;
    drawLine(pdf, MARGIN.left, y, A4.w - MARGIN.right, y, '#cccccc');
    y += 3;
    drawText(pdf, '本提案書はモック版です。実際の数値は現地調査・詳細設計後に確定します。', MARGIN.left, y, { fontSize: 9, color: '#6b7280' });
    drawText(pdf, 'splight株式会社  info@splight.co.jp  https://splight.co.jp/', MARGIN.left, y + 5, { fontSize: 9, color: '#6b7280' });
  }

  function renderSectionHeader(pdf, y, no, title) {
    y = ensureSpace(pdf, y, 14);
    pdf.setFillColor('#2196f3');
    pdf.rect(MARGIN.left, y, 6, 8, 'F');
    drawText(pdf, `${no}. ${title}`, MARGIN.left + 8, y - 0.5, { fontSize: 14, bold: true, color: '#111827' });
    return y + 12;
  }

  function renderKVTable(pdf, y, rows) {
    const colW = (A4.w - MARGIN.left - MARGIN.right) / 2 - 1;
    rows.forEach(([k, v]) => {
      y = ensureSpace(pdf, y, 8);
      drawRect(pdf, MARGIN.left, y, colW, 7, '#f1f5f9');
      drawRect(pdf, MARGIN.left + colW + 2, y, colW, 7, '#ffffff');
      drawText(pdf, k, MARGIN.left + 2, y + 0.5, { fontSize: 10, color: '#374151' });
      drawText(pdf, String(v), MARGIN.left + colW + 4, y + 0.5, { fontSize: 10, color: '#111827' });
      y += 8;
    });
    return y + 2;
  }

  function renderCurrentStatus(pdf, y, cs) {
    y = renderSectionHeader(pdf, y, 1, 'お客様の現状');
    const c = cs.current; const cu = cs.customer;
    const rows = [
      ['お客様区分', cs.segment],
      ['所在地', cu.region || '—'],
      ['業種・建物', cu.type || cu.building || '—'],
      ['契約電力', (c.contract_kw ? c.contract_kw + ' kW' : (cu.ampere || '—'))],
      ['年間電気使用量', fmtNum(c.annual_kwh) + ' kWh'],
      ['年間電気代', fmtJPY(c.annual_bill_jpy)],
      ['買電単価', fmtJPY(c.buy_jpy_per_kwh) + ' / kWh']
    ];
    return renderKVTable(pdf, y, rows);
  }

  function renderProposalConfig(pdf, y, cs) {
    y = renderSectionHeader(pdf, y, 2, 'ご提案構成');
    const p = cs.proposal;
    const rows = [
      ['PV容量', fmtNum(p.pv_kw, 1) + ' kW'],
      ['パネル機種', p.panel_model || '—'],
      ['パネル枚数', fmtNum(p.panel_count) + ' 枚'],
      ['パワコン', p.pcs_model || '—'],
      ['蓄電池', p.battery_kwh ? (fmtNum(p.battery_kwh, 1) + ' kWh / ' + (p.battery_model || '—')) : 'なし'],
      ['設置場所', p.install_place || '—'],
      ['想定年間発電量', fmtNum(p.expected_annual_gen_kwh) + ' kWh'],
      ['自家消費率', fmtNum(p.self_consumption_rate * 100, 1) + ' %']
    ];
    if (p.peak_cut_kw) rows.push(['契約電力削減（ピークカット）', fmtNum(p.peak_cut_kw) + ' kW']);
    return renderKVTable(pdf, y, rows);
  }

  function renderInvestmentSim(pdf, y, cs) {
    y = renderSectionHeader(pdf, y, 3, '投資回収シミュレーション');
    const e = cs.economics;
    const rows = [
      ['初期投資額（税抜）', fmtJPY(e.initial_cost_jpy)],
      ['補助金活用後', fmtJPY(e.net_investment_jpy)],
      ['年間削減額', fmtJPY(e.annual_saving_jpy)],
      ['投資回収年数', fmtNum(e.payback_years, 1) + ' 年'],
      ['20年累積CF', fmtJPY(e.cf_20y_jpy)],
      ['IRR（内部収益率）', fmtNum(e.irr_percent, 1) + ' %']
    ];
    y = renderKVTable(pdf, y, rows);

    // 簡易バーチャート（20年CF推移）: 定額仮定で線形描画
    y = ensureSpace(pdf, y, 40);
    drawText(pdf, '20年キャッシュフロー推移（簡易）', MARGIN.left, y, { fontSize: 10, bold: true });
    y += 6;
    const chartX = MARGIN.left, chartY = y, chartW = A4.w - MARGIN.left - MARGIN.right, chartH = 28;
    drawRect(pdf, chartX, chartY, chartW, chartH, '#f8fafc');
    const annual = e.annual_saving_jpy || 0;
    const init = e.net_investment_jpy || 0;
    const max = init + annual * 20;
    const min = -init;
    const range = max - min || 1;
    const zero = chartY + chartH - ((0 - min) / range) * chartH;
    drawLine(pdf, chartX, zero, chartX + chartW, zero, '#94a3b8');
    for (let i = 0; i <= 20; i++) {
      const cf = -init + annual * i;
      const x = chartX + (i / 20) * chartW;
      const yv = chartY + chartH - ((cf - min) / range) * chartH;
      pdf.setDrawColor(cf >= 0 ? '#16a34a' : '#dc2626');
      pdf.setLineWidth(0.3);
      if (i > 0) {
        const prevCf = -init + annual * (i - 1);
        const prevX = chartX + ((i - 1) / 20) * chartW;
        const prevY = chartY + chartH - ((prevCf - min) / range) * chartH;
        pdf.line(prevX, prevY, x, yv);
      }
    }
    y = chartY + chartH + 4;
    drawText(pdf, '※ 年間削減額の単純線形モデル（モック）。実績はインフレ率・劣化率を反映。', MARGIN.left, y, { fontSize: 8, color: '#6b7280' });
    return y + 6;
  }

  function renderSubsidy(pdf, y, cs) {
    y = renderSectionHeader(pdf, y, 4, '補助金試算（2026年度）');
    const s = cs.subsidy;
    y = drawText(pdf, '活用候補プログラム：', MARGIN.left, y, { fontSize: 11, bold: true });
    (s.programs || []).forEach(p => {
      y = ensureSpace(pdf, y, 8);
      y = drawText(pdf, '・ ' + p, MARGIN.left + 2, y, { fontSize: 10 });
    });
    y += 2;
    drawRect(pdf, MARGIN.left, y, A4.w - MARGIN.left - MARGIN.right, 10, '#fef3c7');
    drawText(pdf, '補助金試算合計', MARGIN.left + 2, y + 1, { fontSize: 11, bold: true });
    drawText(pdf, fmtJPY(s.total_estimate_jpy), A4.w - MARGIN.right - 50, y + 1, { fontSize: 13, bold: true, color: '#d97706' });
    y += 14;
    y = drawText(pdf, '※ 2026年度公募要領未公表のため参考値。一次〆切ありの制度は急ぎ確認のこと。', MARGIN.left, y, { fontSize: 8, color: '#6b7280' });
    return y;
  }

  function renderEnvironment(pdf, y, cs) {
    y = renderSectionHeader(pdf, y, 5, '環境効果');
    const env = cs.environment;
    const rows = [
      ['年間CO2削減量', fmtNum(env.annual_co2_reduction_t, 2) + ' t-CO2'],
      ['20年累積CO2削減量', fmtNum(env.co2_20y_t, 1) + ' t-CO2'],
      ['スギ植林換算（年）', fmtNum(env.tree_equivalent_per_year) + ' 本']
    ];
    y = renderKVTable(pdf, y, rows);
    y = drawText(pdf, '※ 換算係数：0.434 kg-CO2/kWh（環境省2024年度全国平均）／スギ1本=14kg-CO2吸収・年', MARGIN.left, y, { fontSize: 8, color: '#6b7280' });
    return y;
  }

  function renderNextSteps(pdf, y) {
    y = renderSectionHeader(pdf, y, 6, '次のステップ');
    const steps = [
      ['STEP 1', '現地調査（屋根・受電設備の確認／所要30〜60分）'],
      ['STEP 2', '詳細見積のご提出（営業日で3〜5日）'],
      ['STEP 3', '補助金申請書類の作成サポート'],
      ['STEP 4', 'ご契約・発注'],
      ['STEP 5', '施工（規模により1週間〜1か月）'],
      ['STEP 6', '連系・引渡し／保守契約開始']
    ];
    steps.forEach(([n, t]) => {
      y = ensureSpace(pdf, y, 9);
      drawRect(pdf, MARGIN.left, y, 20, 7, '#e0f2fe');
      drawText(pdf, n, MARGIN.left + 2, y + 0.5, { fontSize: 10, bold: true, color: '#0369a1' });
      drawText(pdf, t, MARGIN.left + 24, y + 0.5, { fontSize: 10, color: '#111827' });
      y += 9;
    });
    y += 4;
    drawRect(pdf, MARGIN.left, y, A4.w - MARGIN.left - MARGIN.right, 16, '#f0fdf4');
    drawText(pdf, 'お問い合わせ', MARGIN.left + 3, y + 1, { fontSize: 11, bold: true, color: '#166534' });
    drawText(pdf, 'splight株式会社  EG部', MARGIN.left + 3, y + 6, { fontSize: 10 });
    drawText(pdf, 'Mail: info@splight.co.jp  /  HP: https://splight.co.jp/', MARGIN.left + 3, y + 10, { fontSize: 10 });
    return y + 18;
  }

  // =========================================================================
  // 4. エントリポイント
  // =========================================================================
  async function generateProposalPdf(caseId, meta) {
    meta = meta || {};
    // フォント読込完了を待つ（初回はGoogle Fontsから取得、2回目以降はキャッシュ）
    await ensureFontReady();
    const data = await loadModelCases();
    const cs = (data.cases || []).find(c => c.id === caseId);
    if (!cs) throw new Error('caseId が見つかりません: ' + caseId);

    const pdf = new jsPDF('p', 'mm', 'a4');

    // ページ1: 表紙
    renderCoverPage(pdf, cs, meta);

    // ページ2以降: 内容
    pdf.addPage();
    let y = MARGIN.top;
    y = renderCurrentStatus(pdf, y, cs);
    y = renderProposalConfig(pdf, y, cs);
    y = renderInvestmentSim(pdf, y, cs);
    y = renderSubsidy(pdf, y, cs);
    y = renderEnvironment(pdf, y, cs);
    y = renderNextSteps(pdf, y);

    // ページ番号（表紙は除く全ページ）
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 2; i <= pageCount; i++) {
      pdf.setPage(i);
      drawText(pdf, `- ${i} / ${pageCount} -`, A4.w / 2 - 8, A4.h - 10, { fontSize: 9, color: '#6b7280' });
    }

    const today = new Date().toISOString().slice(0, 10);
    const filename = `splight_proposal_${caseId}_${today}.pdf`;
    pdf.save(filename);
    return filename;
  }

  // モデルケース選択セレクトボックスを初期化
  async function initModelCaseSelector(selectEl) {
    const data = await loadModelCases();
    selectEl.innerHTML = '<option value="">-- モデルケースを選択 --</option>';
    (data.cases || []).forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.id;
      opt.textContent = c.label;
      selectEl.appendChild(opt);
    });
  }
// -------------------------------------------------------------------------
// 公開API (ESM exports)
// -------------------------------------------------------------------------
export { generateProposalPdf, initModelCaseSelector, loadModelCases };
export const SplightProposalPdf = { generateProposalPdf, initModelCaseSelector, loadModelCases };
export default SplightProposalPdf;
